import React from 'react';
import { CircleTopicBaseFragment } from '@modernhealth/circles-graphql/v2';
import { CircleTopicFormat } from '@modernhealth/circles-graphql';
import { CircleCard, CircleCardLink } from '@modernhealth/circles-web-components';
import { filterNullish } from '@modernhealth/filter-nullish';
import { ComponentTextSmall } from '@modernhealth/vital-components';

import { TranslatedCircleFormat } from './TranslatedCircleFormat';
import { TranslatedDuration } from './TranslatedDuration';
import { CircleCardHeaderLink } from './CircleCardHeaderLink';
import { CirclesSeriesEyebrow } from './CircleSessionCard';

export interface CircleStylizedCardProps {
    id: string;
    topic: {
        facilitators: { edges: Array<{ node?: { photo?: string | null } | null } | null> };
        name: string;
        slug?: string | null;
        format: CircleTopicFormat;
        duration?: number;
    };
    series: {
        name: string;
        accessType?: string | null;
    };
    showSeriesLink?: boolean;
}

const CircleStylizedCard: React.FC<CircleStylizedCardProps> = ({
    series,
    topic,
    children,
    id,
    showSeriesLink = false,
}) => {
    const photos = filterNullish(topic?.facilitators.edges.map(edge => edge?.node?.photo) ?? []);

    return (
        <CircleCard
            format={topic.format}
            eyebrow={
                <CirclesSeriesEyebrow series={series} showAccess showSeriesLink={showSeriesLink} />
            }
            photos={photos}
            header={
                <CircleCardHeaderLink
                    name={topic.name}
                    to={`/circles/${topic.slug}`}
                    aria-describedby={children ? `${id}-description` : null}
                />
            }
            body={
                <span id={`${id}-description`}>
                    <ComponentTextSmall marginTop={8}>
                        <TranslatedCircleFormat format={topic.format} />
                        {topic.duration ? (
                            <>
                                {` • `}
                                <TranslatedDuration minutes={topic.duration} />
                            </>
                        ) : null}
                    </ComponentTextSmall>
                </span>
            }
        >
            {children ? (
                <CircleCardLink
                    component={<span id={`${id}-description`} aria-hidden css={{ marginTop: 8 }} />}
                >
                    {children}
                </CircleCardLink>
            ) : null}
        </CircleCard>
    );
};

export const CircleTopicCard: React.FC<{
    topic: Omit<CircleTopicBaseFragment, 'sessions' | 'rsvpQuestions'>;
    id: string;
    showSeriesLink?: boolean;
}> = ({ topic, id, showSeriesLink }) => {
    return (
        <CircleStylizedCard
            topic={topic}
            series={topic.series}
            id={id}
            showSeriesLink={showSeriesLink}
        />
    );
};
